import {
  Badge,
  Card,
  CardActionArea,
  CardActions,
  Fade,
  keyframes,
  Stack,
  styled,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import AssetCardHeader from "./asset-card-header";
import AssetCardBody from "./asset-card-body";
import AssetCardFooter from "./asset-card-footer";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { GetDraggingId } from "../../../Redux/Reducers/redux-slice-layout";
import AssetIcon from "../../Misc/AssetIcon/asset-icon";
import { createPortal } from "react-dom";
import useDraggable from "../../../Utils/Hooks/useDraggable";
import { useSelector } from "react-redux";
import { DriveActions } from "../../Drive/Common/drive-actions";
import { cacheService } from "../../../Core/Api/services";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { TreeContext } from "../../../Redux/Reducers/redux-slice-tree";

export type AssetCardProps = {
  obj: DbAssetObjectInfos;
  selectMode?: boolean;
  selected?: boolean;
  selectedOnClick?: boolean;
  active?: boolean;
  onSelectClick?: (obj: DbAssetObjectInfos) => void;
  onCardClick?: (obj: DbAssetObjectInfos) => void;
  onCardDoubleClick?: (obj: DbAssetObjectInfos) => void;
  onPanelOpen?: () => void;
  onNameChanged?: (name: string) => void;
  onClickInfo?: (obj: DbAssetObjectInfos) => void;
  subtitle?: string;
  shareBtn?: boolean;
  orderBtn?: boolean;
  renameBtn?: boolean;
  trashBtn?: boolean;
  infoBtn?: boolean;
  downloadBtn?: boolean;
  labelList?: string[];
  isCombiningSource?: boolean;
  isCombiningTarget?: boolean;
  scale?: number;
  autoScroll?: boolean;
  selectedItems?: DbAssetObjectInfos[];
  draggable?: boolean;
  showDriveActions?: boolean;
  sx?: SxProps;
};

const AssetCard: FC<AssetCardProps> = (props) => {
  const {
    selected,
    active,
    onSelectClick,
    onCardClick,
    onCardDoubleClick,
    onPanelOpen,
    onNameChanged,
    onClickInfo,
    selectMode = true,
    selectedOnClick,
    obj,
    shareBtn,
    orderBtn,
    renameBtn,
    trashBtn,
    infoBtn,
    downloadBtn,
    subtitle,
    showDriveActions = false,
    isCombiningSource,
    isCombiningTarget,
    scale = GlobalConst.DEFAULT_SCALE,
    autoScroll = false,
    selectedItems,
    draggable = false,
    sx,
  } = props;

  const location = useLocation();
  const mode = cacheService.GetPlatformMode();

  const { isDragging, isDragOver, state, ref } = useDraggable(
    obj.type,
    obj.id,
    true,
    "card",
    draggable ? "both" : "none",
    selectedItems?.map((x) => x.id),
    TreeContext.DriveTreeview
  );

  const [hover, setHover] = useState<boolean>(false);

  useEffect(() => {
    if (ref?.current && location.state?.retrieveId && location.state?.retrieveId === props.obj.id) {
      ref?.current.scrollIntoView();
    }
  }, [ref?.current, location.state]);

  const isUploading = useMemo(() => obj.id === -500 && obj.status === ObjectStatus.Undefined, [obj.id, obj.status]);

  const handleCardClick: React.MouseEventHandler = useCallback(
    (e) => {
      if ((selectedOnClick || e.ctrlKey) && onSelectClick) {
        onSelectClick(obj);
      } else {
        onCardClick && onCardClick(obj);
      }
    },
    [onSelectClick, selectedOnClick, obj]
  );

  const handleCardDoubleClick: React.MouseEventHandler = useCallback(
    (e) => {
      if (onCardDoubleClick) {
        e.preventDefault();
        onCardDoubleClick(obj);
      }
    },
    [onCardDoubleClick, obj]
  );

  const theme = useTheme();

  const dragging = useSelector(GetDraggingId);

  return (
    <>
      <StyledLink
        isUploading={isUploading}
        ref={ref}
        className="MuiMovieCard"
        selected={selected}
        active={active}
        targeted={isDragOver}
        isDragging={(selectedItems?.find((x) => x.id === obj.id) && dragging && dragging > 0) || isDragging}
        scale={scale}
        onMouseEnter={(e) => setHover(true)}
        onMouseLeave={(e) => setHover(false)}
      >
        <CardActionArea
          disabled={isUploading}
          onClick={handleCardClick}
          onDoubleClick={handleCardDoubleClick}
          sx={(theme) => ({
            padding: theme.spacing(1),
            paddingBottom: "0px",
          })}
        >
          {selectMode && (hover! || selected!) && (
            <AssetCardHeader onSelectClick={onSelectClick} selected={selected} obj={obj} />
          )}
          <AssetCardBody
            obj={obj}
            isCombiningSource={isCombiningSource}
            selected={selected}
            isUploading={isUploading}
            scale={scale}
            autoScroll={autoScroll}
          />
          <AssetCardFooter
            obj={obj}
            trashBtn={trashBtn}
            onClickInfo={onClickInfo}
            onNameChanged={onNameChanged}
            shareBtn={shareBtn}
            renameBtn={renameBtn}
            orderBtn={orderBtn}
            infoBtn={infoBtn}
            downloadBtn={downloadBtn}
            subtitle={subtitle}
            scale={scale}
          />
        </CardActionArea>
        {showDriveActions && (
          <Fade in={hover}>
            <CardActions
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                p: 1,
              }}
            >
              <DriveActions object={obj} objId={obj.id} openPanel={onPanelOpen} context="card" />
            </CardActions>
          </Fade>
        )}
      </StyledLink>
      {state.type === "preview"
        ? createPortal(
            <Badge
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              color="warning"
              badgeContent={
                selectedItems && selectedItems.length > 0
                  ? selectedItems.length + (selectedItems.find((x) => x.id === obj.id) ? 0 : 1)
                  : 1
              }
            >
              <Stack
                sx={{ background: theme.palette.background.paper }}
                borderTop={`2px solid ${theme.palette.backgroundAlpha.border}`}
                p={1}
                borderRadius={1}
                spacing={1}
                maxWidth={"175px"}
                direction={"row"}
                height={"32px"}
                alignItems={"center"}
              >
                <AssetIcon colored type={obj.type} />

                <Typography
                  variant="body2"
                  color={theme.palette.getContrastText(theme.palette.background.paper)}
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                  noWrap
                >
                  {obj.name}
                </Typography>
              </Stack>
            </Badge>,
            state.container
          )
        : null}
    </>
  );
};

export default AssetCard;

const blinker = keyframes`0% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }`;

const StyledLink = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== "selected" &&
    prop !== "active" &&
    prop !== "targeted" &&
    prop !== "isUploading" &&
    prop !== "scale" &&
    prop !== "isDragging",
})<{
  sx?: SxProps;
  selected?: boolean;
  active?: boolean;
  targeted?: boolean;
  status?: ObjectStatus;
  isUploading?: boolean;
  scale: number;
  isDragging?: boolean;
}>(({ theme, selected, active, targeted, scale, isUploading = false, isDragging }) => ({
  width: "100%",
  position: "relative",
  display: "block",
  animation: isUploading ? `${blinker} 1s cubic-bezier(.5, 0, 1, 1) infinite alternate` : "",
  border: isUploading
    ? `5px dashed ${theme.palette.primary.alphaHover}`
    : `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: "none",
  height: "100%",
  boxShadow: "none",
  borderRadius: 6,
  overflow: "hidden",
  color: theme.palette.text.primary,
  opacity: isDragging ? 0.25 : 1,
  paddingBottom: "0px",
  boxSizing: "border-box",
  minWidth: 0 /* NEW; needed for Firefox */,
  transition: theme.transitions.create(["border-color", "background-color", "box-shadow", "transform"], {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeOut,
  }),
  "&:hover": {
    transform: "scale(1.04)",
    ...theme.helpers(theme).popoverMenuHighlights,

    ".MuiCheckbox-Bg": {
      opacity: 0.5,
    },

    ".CardMoreMenu": {
      display: "block",
    },
    ".action-menu": {
      opacity: 1,
    },
    ".child-count": {
      opacity: 0,
    },
  },
  ".MuiCardActionArea-root": {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    height: "100%",
  },
  ".MuiCardActionArea-focusHighlight": {
    display: "none",
  },

  ...(selected && {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  }),
  ...(active && {
    backgroundColor: theme.palette.primary.alpha,
  }),
  ...(targeted && {
    border: `2px dashed ${theme.palette.primary.main}`,
  }),
}));

export const AssetCardGrid = styled("div", {
  shouldForwardProp: (prop) => prop !== "scale",
})<{ scale: number }>(({ scale }) => ({
  display: "grid",
  gridColumnGap: "16px",
  gridRowGap: "16px",
  marginTop: "8px",
  paddingBottom: "16px",
  gridTemplateColumns: `repeat(${10 - scale}, minmax(0, 1fr))`,
  img: {
    width: "100%",
  },
}));
